import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// A request with no timeout can hang forever on a stalled connection (common on mobile) — and a
// promise that never settles means `finally` never runs, so a page's own error handling can't save
// it. A global 30s cap converts every silent hang into a rejection any caller can surface.
export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 30000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("os_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Server-side beta gate: if a marketplace read returns 403 {error:"beta_gate"} (e.g. the
// beta cookie expired, or someone forged the localStorage curtain), drop the stale unlock
// flag and bounce to the splash so they re-enter the code (which re-issues the cookie).
let _betaBounced = false;
// Auth-death guard: a 401 on any AUTHENTICATED call means the token is dead (expired/invalidated
// server-side). We route straight to the logged-out path — callers must NEVER paint an empty or
// "not measured" state over an auth failure. Exempt the credential endpoints themselves (a bad
// login/reset is a 401 the form must show, not a redirect loop).
let _authBounced = false;
const AUTH_EXEMPT = ["/auth/login", "/auth/register", "/auth/google", "/auth/forgot-password", "/auth/reset-password", "/auth/verify-email", "/auth/send-verification"];
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const d = error?.response?.data?.detail;
    const isBetaGate = status === 403 && d && typeof d === "object" && d.error === "beta_gate";
    if (isBetaGate && !_betaBounced && typeof window !== "undefined") {
      _betaBounced = true;
      try { localStorage.removeItem("os_beta_unlocked"); } catch (e) {}
      window.location.reload();
      return Promise.reject(error);
    }
    const hadToken = !!(typeof localStorage !== "undefined" && localStorage.getItem("os_token"));
    // Profile-completion gate: the server returns 428 on list/sell/message when the account has
    // no real name yet. Open the name prompt (NameGate listens) instead of only toasting an error.
    if (status === 428 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("os:name-required", { detail: { message: typeof d === "string" ? d : "" } }));
    }
    const exempt = AUTH_EXEMPT.some((p) => url.includes(p));
    if (status === 401 && hadToken && !exempt && !_authBounced && typeof window !== "undefined") {
      _authBounced = true;
      // Clear ALL client auth state — including super/admin mode, so signing back in never
      // silently restores an elevated context.
      try {
        localStorage.removeItem("os_token");
        sessionStorage.removeItem("os_admin_mode");
      } catch (e) {}
      const path = window.location.pathname || "/";
      // Don't stack a next= for auth pages; otherwise return the user to where they were.
      const next = path.startsWith("/login") || path === "/" ? "" : `&next=${encodeURIComponent(path + window.location.search)}`;
      window.location.assign(`/login?expired=1${next}`);
    }
    return Promise.reject(error);
  }
);

const GENERIC_ERROR = "Something went wrong. Please try again.";

// A response body we should NEVER paint into the UI: an edge/origin error page (Cloudflare 52x,
// nginx/gateway HTML), an empty body, or anything suspiciously long/markup-y. These reach the
// frontend as a raw string when an origin returns malformed/empty 5xx — surfacing them leaks scary
// "Cloudflare could not parse…" text into product UI. Collapse them to a calm, human message.
function looksLikeRawError(s) {
  const t = (s || "").trim();
  if (!t) return true;
  const low = t.toLowerCase();
  return (
    t.length > 240 ||
    low.startsWith("<") ||
    low.includes("<html") || low.includes("<!doctype") || low.includes("<body") ||
    low.includes("cloudflare") || low.includes("ray id") || low.includes("error code") ||
    low.includes("origin web server") || low.includes("could not parse") ||
    low.includes("bad gateway") || low.includes("gateway time") || low.includes("service unavailable")
  );
}

export function formatError(detail) {
  if (detail == null) return GENERIC_ERROR;
  if (typeof detail === "string") return looksLikeRawError(detail) ? GENERIC_ERROR : detail;
  if (Array.isArray(detail)) {
    const msg = detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ");
    return msg && !looksLikeRawError(msg) ? msg : GENERIC_ERROR;
  }
  if (detail && typeof detail.msg === "string") return looksLikeRawError(detail.msg) ? GENERIC_ERROR : detail.msg;
  if (detail && typeof detail.message === "string") return looksLikeRawError(detail.message) ? GENERIC_ERROR : detail.message;
  // Never String()-dump an unknown object/HTML into the UI.
  return GENERIC_ERROR;
}

// One place to turn any failed axios load into a human message for a LoadFailed state. Distinguishes
// a timeout (ECONNABORTED) from a server error and from a network drop, so "failed" never looks like
// "empty" or an endless spinner.
export function loadError(err, fallback = "Couldn't load.") {
  if (err?.code === "ECONNABORTED") return "Timed out — the server took too long to respond. Retry.";
  const d = err?.response?.data?.detail;
  if (d != null) return formatError(d);
  if (err?.response?.status) return `Request failed (${err.response.status}).`;
  return err?.message || fallback;
}

// Email validation aligned with the browser's native type=email check: rejects
// whitespace, emoji and other non-ASCII, control chars, AND the backtick (`),
// which our server's validator also rejects. Server-side stays authoritative.
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
export function isValidEmail(email) {
  const v = (email || "").trim();
  return !!v && v.length <= 254 && EMAIL_RE.test(v);
}

// Map a FastAPI error payload into per-field errors + a leftover general message.
// FastAPI 422 shape: [{ loc: ["body", "<field>"], msg, type }, ...]. Anything
// that isn't tied to a body field (or a plain string 400/500 detail) lands in `general`.
export function toFieldErrors(detail) {
  const fields = {};
  const generalParts = [];
  if (Array.isArray(detail)) {
    for (const e of detail) {
      const loc = Array.isArray(e?.loc) ? e.loc : [];
      const field = [...loc].reverse().find((s) => typeof s === "string" && s !== "body");
      const msg = e && typeof e.msg === "string" ? e.msg : "Invalid value.";
      if (field) fields[field] = msg;
      else generalParts.push(msg);
    }
  } else if (typeof detail === "string") {
    generalParts.push(detail);
  } else if (detail && typeof detail.msg === "string") {
    generalParts.push(detail.msg);
  }
  return { fields, general: generalParts.join(" ") };
}
